import React, { useEffect, useRef, useState } from "react";
import { classnames, Icon } from "@culturehq/components";
import { Trans } from "app/utils";
import { Organization } from "chq";
import LazyInputRange from "components/survey/item-types/LazyInputRange";
import { CurrentOption } from "./EffectsSDKCameraRecorder";
import effectsItem from "../effects-items.json";
import Carousel from "../Carousel";
import BackgroundsIcon from "../icons/BackgroundsIcon";

interface BackgroundOptionsProps {
  currentOption: CurrentOption;
  effectsSDK: any;
  isDisableOption: (option: CurrentOption) => boolean;
  onSelectOption: (option: CurrentOption) => void;
  open: boolean;
  organization: Organization;
}

const BackgroundOptions: React.FC<BackgroundOptionsProps> = ({
  currentOption,
  effectsSDK,
  isDisableOption,
  onSelectOption,
  open,
  organization
}) => {
  const [backgrounds, setBackgrounds] = useState<any[]>(effectsItem.background);
  const [backgroundSelection, setBackgroundSelection] = useState("None");
  const [blur, setBlur] = useState(0);
  const backgroundFileInputRef = useRef<HTMLInputElement>(null);

  const defaultBackgrounds = () => backgrounds.filter(bg => bg.name === "None" || bg.name === "Blur");

  useEffect(() => {
    if (!open) {
      setBackgroundSelection("None");
      setBackgrounds(effectsItem.background);
      setBlur(0);
    }
  }, [open]);

  useEffect(() => {
    if (!organization.virtualBackgrounds?.length) return;

    const noneAndBlur = defaultBackgrounds();
    setBackgrounds([
      ...noneAndBlur,
      ...organization.virtualBackgrounds.map((vbg: any) => ({
        media: vbg.fullUrl || vbg.url,
        thumbnail: vbg.thumbnail || vbg.smallUrl || vbg.url,
        video: vbg.mediaType === "video",
        name: vbg.url.split("/").pop(),
        localFile: false,
        displayName: false
      }))]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, organization.virtualBackgrounds]);

  const createVideoThumbnail = (videoFile: File): Promise<string> => new Promise(resolve => {
    const video = document.createElement("video");
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    video.src = URL.createObjectURL(videoFile);
    video.load();

    video.addEventListener("loadeddata", () => {
      video.currentTime = 0.1;
      effectsSDK.setBackground(video);
    });

    video.addEventListener("seeked", () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // eslint-disable-next-line no-unused-expressions
      (ctx?.drawImage(video, 0, 0, canvas.width, canvas.height));

      const thumbnailUrl = canvas.toDataURL("image/jpeg");
      URL.revokeObjectURL(video.src);

      resolve(thumbnailUrl);
    });
  });

  const handleBackgroundChange = (backgroundItem: any) => {
    setBackgroundSelection(backgroundItem.name);
    switch (backgroundItem.name) {
      case "None":
        if (effectsSDK) {
          effectsSDK.clearBackground();
          effectsSDK.clearBlur();
          setBlur(0);
        }
        break;
      case "Blur":
        if (effectsSDK && blur === 0) {
          effectsSDK.clearBackground();
          setBlur(50);
          effectsSDK.setBlur(50 / 100);
        }
        break;
      default:
        if (effectsSDK) {
          effectsSDK.clearBlur();
          if (backgroundItem.localFile && backgroundItem.video) {
            const video = document.createElement("video");
            video.src = backgroundItem.media;
            video.addEventListener("loadeddata", () => {
              effectsSDK.setBackground(video);
            });
          } else {
            effectsSDK.setBackground(`${backgroundItem.media}?timestamp=${Date.now()}`);
          }
          setBlur(0);
        }
        break;
    }
  };

  const handleFileInputChange = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    if (target.files) {
      const file = Array.from(target.files)[0];
      const media = file;
      const mediaUrl = URL.createObjectURL(media);
      effectsSDK.clearBlur();
      setBackgroundSelection("Input");
      setBlur(0);

      const noneAndBlur = defaultBackgrounds();
      const otherBackgrounds = backgrounds.filter(bg => bg.name !== "None" && bg.name !== "Blur");

      if (file.type.startsWith("image/")) {
        effectsSDK.setBackground(mediaUrl);

        setBackgrounds([
          ...noneAndBlur,
          {
            localFile: true,
            name: file.name,
            media: mediaUrl,
            displayName: false
          },
          ...otherBackgrounds
        ]);
      }

      if (file.type.startsWith("video/")) {
        const video = document.createElement("video");
        video.src = mediaUrl;
        const thumbnailUrl = await createVideoThumbnail(file);

        setBackgrounds([
          ...noneAndBlur,
          {
            localFile: true,
            name: file.name,
            media: mediaUrl,
            displayName: false,
            video: true,
            thumbnail: thumbnailUrl
          },
          ...otherBackgrounds
        ]);
      }
      setBackgroundSelection(file.name);
    }
  };

  return (
    <div
      data-testid="background-options"
      className={classnames("Options-Container__effects", isDisableOption("backgrounds") ? "disabled" : "")}
    >
      {currentOption === "backgrounds" && (
        <div
          data-testid="background-options-menu"
          className="option-menu option-menu__background chq-ffd--sl--opts chq-ffd--sl--opts-open no-scrollbar"
        >
          <Carousel data={backgrounds} elementWidth={107}>
            <div className="items-container">
              <div
                data-testid="background-upload-button"
                className={classnames("background-item", "invert-icon", backgroundSelection === "Input" ? "background-item__active" : "")}
                onClick={() => {
                  if (backgroundFileInputRef.current) {
                    backgroundFileInputRef.current.click();
                  }
                }}
              >
                <input
                  ref={backgroundFileInputRef}
                  type="file"
                  id="fileInput"
                  onChange={handleFileInputChange}
                  accept="image/*,video/*"
                  style={{ display: "none" }}
                />
                <Icon icon="download" />
                <Trans>Upload</Trans>
              </div>
              <div className="vertical-divider" />
              {backgrounds.map((item, idx) => (
                <div
                  key={idx}
                  data-testid={`background-item-${item.name}`}
                  className={classnames("background-item", backgroundSelection === item.name ? "background-item__active" : "")}
                  style={{
                    backgroundImage: item.video && !item.thumbnail
                      ? "none"
                      : `url(${item.thumbnail || item.media})`,
                    backgroundColor: "#f3f3f3"
                  }}
                  onClick={() => { handleBackgroundChange(item); }}
                >
                  {item.displayName
                  && <span><Trans>{item.name}</Trans></span>}
                  {item.video && (
                    <div className="video-indicator">
                      <Icon icon="play-filled" />
                    </div>
                  )}
                  {item.range && (
                    <div className="range-bar">
                      <LazyInputRange
                        data-testid="blur-slider"
                        maxValue={100}
                        minValue={0}
                        value={blur}
                        onChange={(range: any) => {
                          if (range > 0) {
                            setBackgroundSelection("Blur");
                            effectsSDK.clearBackground();
                            setBlur(range);
                            effectsSDK.setBlur(range / 100);
                          } else {
                            effectsSDK.clearBlur();
                            setBlur(0);
                            setBackgroundSelection("None");
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Carousel>
        </div>
      )}
      <div
        data-testid="background-option-button"
        className="option"
        onClick={() => onSelectOption("backgrounds")}
      >
        <div className="icon-container">
          {currentOption === "backgrounds" ? (
            <Icon className="icon-container__icon" icon="close" />
          ) : <BackgroundsIcon className="icon-container__icon" />}
        </div>
        <span className="option-legend"><Trans>Backgrounds</Trans></span>
      </div>
      <div
        data-testid="background-option-arrow"
        className="arrow"
        onClick={() => onSelectOption("backgrounds")}
      >
        <span aria-hidden="true" className={classnames("chq-dd--caret video-effects", { "chq-dd--caret-ex ": currentOption === "backgrounds" })} />
      </div>
    </div>
  );
};

export default BackgroundOptions;
